import * as React from "react";
import { IoBarChartOutline } from "react-icons/io5";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { IAdvisorComparisonTableBlock } from "@types";

import { cn } from "../cn";
import {
  abbreviate,
  axisColumns,
  buildChart,
  ChartTooltip,
  isEstimatedColumn,
  seriesColor,
  ViewToggle,
  // type DistributionView,
} from "./chartCommon";

/**
 * S1A_MARKET's market percentiles, rendered two ways: a grid of per-component cards by
 * default, or a grouped bar chart of the same numbers, switched by the toggle in the
 * top-right. Each card is one component (Annual Base, Total Guaranteed Cash, ...) with its
 * coloured marker, listing P25 / P50 / P75 / P90 (est.). The estimated column is de-emphasised.
 */

function CardsView({ block }: { block: IAdvisorComparisonTableBlock }) {
  const axis = axisColumns(block);

  return (
    <div className="grid gap-3 sm:grid-cols-2">
      {block.rows.map((row, r) => (
        <div
          key={r}
          className="rounded-xl border border-gray-200 bg-white px-4 py-3 shadow-sm"
        >
          <div className="mb-3 flex items-center gap-2.5">
            <span
              className={cn(
                "h-2.5 w-2.5 shrink-0 rounded-sm",
                seriesColor(r).dot
              )}
            />
            <span className="text-sm font-semibold text-gray-800">
              {row.cells[0]}
            </span>
          </div>
          <div className="flex flex-col gap-2.5">
            {axis.map(c => {
              // The fitted P90 is an estimate, not an observation — it sits back a shade.
              const est = isEstimatedColumn(block.columns[c] ?? "");
              return (
                <div
                  key={c}
                  className="flex items-center justify-between gap-4"
                >
                  <span
                    className={cn(
                      "text-sm",
                      est ? "text-gray-400" : "text-gray-500"
                    )}
                  >
                    {block.columns[c]}
                  </span>
                  <span
                    className={cn(
                      "text-sm font-semibold",
                      est ? "text-gray-500" : "text-gray-800"
                    )}
                  >
                    {row.cells[c]}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

function BarChartView({ block }: { block: IAdvisorComparisonTableBlock }) {
  const { series, data } = buildChart(block);

  return (
    <div className="rounded-xl border border-gray-200 bg-white px-1 pb-2 pt-3">
      <ResponsiveContainer width="100%" height={300}>
        <BarChart
          data={data}
          margin={{ top: 8, right: 20, bottom: 4, left: 4 }}
          barGap={2}
          barCategoryGap="22%"
        >
          <CartesianGrid vertical={false} stroke="#E5E7EB" />
          <XAxis
            dataKey="axis"
            tickLine={false}
            axisLine={false}
            dy={8}
            tick={{ fontSize: 12, fill: "#6B7280" }}
          />
          <YAxis
            width={48}
            tickLine={false}
            axisLine={false}
            tickFormatter={abbreviate}
            tick={{ fontSize: 12, fill: "#6B7280" }}
          />
          <Tooltip content={<ChartTooltip />} cursor={{ fill: "#F3F4F6" }} />
          {series.map(s => (
            <Bar
              key={s.label}
              dataKey={s.label}
              fill={s.stroke}
              radius={[3, 3, 0, 0]}
              maxBarSize={26}
              isAnimationActive={false}
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

export function MarketPercentileCards({
  block,
}: {
  block: IAdvisorComparisonTableBlock;
}) {
  const [view, setView] = React.useState<DistributionView>("table");

  return (
    // Pull up slightly to sit closer to the preceding intro text, and keep the toggle tight to
    // the content below it.
    <div className="-mt-1 flex flex-col gap-1.5">
      <div className="flex justify-end">
        <ViewToggle
          view={view}
          onChange={setView}
          chartIcon={<IoBarChartOutline className="h-4 w-4" />}
        />
      </div>
      {view === "table" ? (
        <CardsView block={block} />
      ) : (
        <BarChartView block={block} />
      )}
    </div>
  );
}
